/**
 * Bus Stop Service
 * Find nearest stops and get departures
 */

import { Logger } from '@utils/logger';
import { getConfig } from '@config/index';
import { GeolocationService } from '@core/geolocation';
import { BusStopCache } from './cache';
import { BusStopError } from './errors';
import { fetchChelmsfordBusStops } from '@api/naptan';
import { fetchDeparturesForStop } from '@api/departures';
import type { BusStop, Coordinates, NearbyBusStop, DepartureBoard } from '@/types';
import { BusStopErrorCode } from '@/types';

/**
 * Partial failure info for a single stop
 */
interface StopFetchError {
    stop: NearbyBusStop;
    error: string;
}

/**
 * Result type for both directions (supports partial success)
 */
type BothDirectionsResult =
    | { success: true; boards: DepartureBoard[]; partialFailures?: StopFetchError[] }
    | { success: false; error: BusStopError };

/**
 * Get opposite bearing direction
 */
function getOppositeBearing(bearing: string | undefined): string | undefined {
    if (!bearing) return undefined;
    const opposites: Record<string, string> = {
        N: 'S',
        S: 'N',
        E: 'W',
        W: 'E',
        NE: 'SW',
        SW: 'NE',
        NW: 'SE',
        SE: 'NW',
    };
    return opposites[bearing.toUpperCase()];
}

/**
 * Filter nearby stops down to up to 2 in the nearest stop's bearing and up to 2 in the
 * opposite bearing - the "both directions" curated list shared by getBothDirections and
 * refreshBothDirections.
 */
function selectBothDirectionStops(nearbyStops: NearbyBusStop[]): NearbyBusStop[] {
    const nearest = nearbyStops[0];
    const primaryBearing = nearest.bearing?.toUpperCase();
    const oppositeBearing = getOppositeBearing(nearest.bearing);

    const primaryStops = nearbyStops.filter(stop => stop.bearing?.toUpperCase() === primaryBearing);
    const oppositeStops = oppositeBearing
        ? nearbyStops.filter(stop => stop.bearing?.toUpperCase() === oppositeBearing)
        : nearbyStops.filter(
              stop => stop.bearing && stop.bearing?.toUpperCase() !== primaryBearing
          );

    return [...primaryStops.slice(0, 2), ...oppositeStops.slice(0, 2)];
}

/**
 * Fetch departure boards for a set of stops in parallel, splitting fulfilled boards from
 * failures instead of letting one bad stop fail the whole batch.
 */
async function fetchBoardsWithPartialFailures(
    stopsToShow: NearbyBusStop[],
    fetchBoard: (stop: NearbyBusStop) => Promise<DepartureBoard>
): Promise<{ boards: DepartureBoard[]; partialFailures: StopFetchError[] }> {
    const results = await Promise.allSettled(stopsToShow.map(fetchBoard));

    const boards: DepartureBoard[] = [];
    const partialFailures: StopFetchError[] = [];

    results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
            boards.push(result.value);
        } else {
            const stop = stopsToShow[index];
            const errorMessage =
                result.reason instanceof Error ? result.reason.message : String(result.reason);
            partialFailures.push({ stop, error: errorMessage });
            Logger.warn('Failed to fetch departures for stop', {
                atcoCode: stop.atcoCode,
                error: errorMessage,
            });
        }
    });

    return { boards, partialFailures };
}

/**
 * Drop boards with no departures, then dedupe same-direction stops sharing lines.
 * Shared by getBothDirections and refreshBothDirections so a refresh shows the same
 * set of stops as the initial load.
 */
function selectUsefulBoards(boards: DepartureBoard[]): DepartureBoard[] {
    return deduplicateBySharedLines(boards.filter(b => b.departures.length > 0));
}

/** Stops loaded by init(), kept in memory in case IndexedDB is unavailable */
let loadedStops: BusStop[] | null = null;

/** In-flight init() load, so lookups made during startup can wait for it */
let initPromise: Promise<void> | null = null;

/**
 * Get all bus stops: from the IndexedDB cache, or else from the stops loaded by init()
 * (waiting for that load if it's still in progress)
 */
async function getAllStops(): Promise<BusStop[] | null> {
    const cached = await BusStopCache.getStops();
    if (cached && cached.length > 0) return cached;

    if (initPromise) await initPromise;
    return loadedStops;
}

/**
 * Removes redundant stops going the same direction that share bus lines.
 * Keeps the nearest stop when duplicates are found.
 * Logic: if the same bus serves both stops, showing it twice is redundant.
 */
export function deduplicateBySharedLines(boards: DepartureBoard[]): DepartureBoard[] {
    const result: DepartureBoard[] = [];

    for (const board of boards) {
        const lines = new Set(board.departures.map(d => d.line));

        // Check if we already have a stop with same bearing that shares any lines
        const isDuplicate = result.some(existing => {
            // Must be same direction
            if (existing.stop.bearing !== board.stop.bearing) return false;

            // Check if any lines overlap
            const existingLines = new Set(existing.departures.map(d => d.line));
            for (const line of lines) {
                if (existingLines.has(line)) return true; // Found shared line
            }
            return false;
        });

        if (!isDuplicate) {
            result.push(board);
        }
    }

    return result;
}

/**
 * BusStopService - Find nearest stops and departures
 */
export const BusStopService = {
    /**
     * Initialize cache with NAPTAN data
     * Call once at app startup (non-blocking)
     */
    async init(): Promise<void> {
        initPromise = (async () => {
            const cached = await BusStopCache.getStops();
            if (cached && cached.length > 0) {
                Logger.debug('Bus stops loaded from cache', { count: cached.length });
                return;
            }

            try {
                Logger.debug('Fetching bus stops from NAPTAN...');
                const stops = await fetchChelmsfordBusStops();
                loadedStops = stops;
                await BusStopCache.setStops(stops);
                Logger.success('Bus stops cached', { count: stops.length });
            } catch (error) {
                Logger.warn('Failed to fetch bus stops, will retry later', error);
                // Non-fatal - user can still use app with cached data or retry
            }
        })();
        await initPromise;
    },

    /**
     * Find nearest bus stops to given coordinates
     * @param location - User's current coordinates
     * @param maxResults - Maximum number of stops to return (default: 1)
     * @param customRadius - Optional custom search radius in meters (overrides config)
     * @returns Array of nearby bus stops sorted by distance
     */
    async findNearest(
        location: Coordinates,
        maxResults = 1,
        customRadius?: number
    ): Promise<NearbyBusStop[]> {
        const stops = await getAllStops();

        if (!stops || stops.length === 0) {
            throw new BusStopError(
                'No bus stops available - try refreshing',
                BusStopErrorCode.NO_STOPS_FOUND
            );
        }

        const config = getConfig();
        const maxRadius = customRadius ?? config.busStops.maxSearchRadius;

        // Calculate distances and filter by radius
        const nearby: NearbyBusStop[] = stops
            .map(stop => ({
                ...stop,
                distanceMeters: GeolocationService.calculateDistance(location, stop.coordinates),
            }))
            .filter(stop => stop.distanceMeters <= maxRadius)
            .sort((a, b) => a.distanceMeters - b.distanceMeters)
            .slice(0, maxResults);

        if (nearby.length === 0) {
            throw new BusStopError(
                `No bus stops within ${maxRadius}m - try a different location`,
                BusStopErrorCode.NO_STOPS_FOUND
            );
        }

        Logger.debug('Found nearby stops', {
            count: nearby.length,
            nearest: nearby[0]?.commonName,
            distance: nearby[0]?.distanceMeters,
        });

        return nearby;
    },

    /**
     * Get stops by their ATCO codes (for favorites)
     * @param atcoCodes - Array of ATCO codes to look up
     * @param location - User's current coordinates (for distance calculation)
     * @returns Array of stops with distances (regardless of search radius)
     */
    async getByAtcoCodes(atcoCodes: string[], location: Coordinates): Promise<NearbyBusStop[]> {
        if (atcoCodes.length === 0) return [];

        const stops = await getAllStops();
        if (!stops || stops.length === 0) return [];

        const atcoSet = new Set(atcoCodes);
        return stops
            .filter(stop => atcoSet.has(stop.atcoCode))
            .map(stop => ({
                ...stop,
                distanceMeters: GeolocationService.calculateDistance(location, stop.coordinates),
            }));
    },

    /**
     * Get nearest stops in both directions
     * Returns 2 stops per direction (4 total) - nearest and next nearest for each
     */
    async getBothDirections(location: Coordinates): Promise<BothDirectionsResult> {
        Logger.debug('Getting departures for both directions', { location });
        try {
            // Get many nearby stops to find multiple in each direction
            const nearbyStops = await this.findNearest(location, 50);
            Logger.debug('Found nearby stops', { count: nearbyStops.length });

            if (nearbyStops.length === 0) {
                throw new BusStopError(
                    'No bus stops found nearby',
                    BusStopErrorCode.NO_STOPS_FOUND
                );
            }

            const stopsToShow = selectBothDirectionStops(nearbyStops);

            // Fetch departures for all stops in parallel with partial success handling
            const { boards, partialFailures } = await fetchBoardsWithPartialFailures(
                stopsToShow,
                stop => this.getDeparturesForStop(stop)
            );

            // Return success if at least one stop has departures
            const usefulBoards = selectUsefulBoards(boards);
            if (usefulBoards.length > 0) {
                return {
                    success: true,
                    boards: usefulBoards,
                    partialFailures: partialFailures.length > 0 ? partialFailures : undefined,
                };
            }

            // All failed
            throw new BusStopError(
                'Failed to fetch departures for all stops',
                BusStopErrorCode.DEPARTURES_UNAVAILABLE
            );
        } catch (error) {
            const busError =
                error instanceof BusStopError
                    ? error
                    : new BusStopError(String(error), BusStopErrorCode.DEPARTURES_UNAVAILABLE);

            return { success: false, error: busError };
        }
    },

    /**
     * Helper to get departures for a single stop
     */
    async getDeparturesForStop(stop: NearbyBusStop): Promise<DepartureBoard> {
        Logger.debug('Getting departures for stop', {
            atcoCode: stop.atcoCode,
            name: stop.commonName,
        });

        // Try cache first
        const cached = await BusStopCache.getDepartures(stop.atcoCode);
        if (cached && cached.length > 0) {
            Logger.debug('Using cached departures', {
                atcoCode: stop.atcoCode,
                count: cached.length,
            });
            return {
                stop,
                departures: cached,
                lastUpdated: Date.now(),
                isStale: true,
            };
        }

        // Fetch fresh from BODS
        try {
            Logger.debug('Fetching fresh departures from BODS', { atcoCode: stop.atcoCode });
            const departures = await fetchDeparturesForStop(stop, 3);
            Logger.debug('Fetched departures', {
                atcoCode: stop.atcoCode,
                count: departures.length,
            });
            await BusStopCache.setDepartures(stop.atcoCode, departures);

            return {
                stop,
                departures,
                lastUpdated: Date.now(),
                isStale: false,
            };
        } catch (error) {
            Logger.warn('Failed to fetch departures for stop', { atcoCode: stop.atcoCode, error });
            return {
                stop,
                departures: [],
                lastUpdated: Date.now(),
                isStale: false,
            };
        }
    },

    /**
     * Refresh departures for a single stop (bypasses cache, fetches fresh)
     */
    async refreshDeparturesForStop(stop: NearbyBusStop): Promise<DepartureBoard> {
        try {
            const departures = await fetchDeparturesForStop(stop, 3);
            await BusStopCache.setDepartures(stop.atcoCode, departures);
            return {
                stop,
                departures,
                lastUpdated: Date.now(),
                isStale: false,
            };
        } catch (error) {
            Logger.warn('Failed to refresh departures for stop', {
                atcoCode: stop.atcoCode,
                error,
            });
            return {
                stop,
                departures: [],
                lastUpdated: Date.now(),
                isStale: false,
            };
        }
    },

    /**
     * Get expanded list of stops (for "Show more" feature)
     * Returns stops not already displayed, with progressive radius expansion
     * @param location - User's current coordinates
     * @param excludeAtcoCodes - ATCO codes to exclude (already displayed)
     * @param currentRadius - Current search radius (will expand from here)
     * @param maxResults - Maximum number of stops to return (default: 6)
     * @returns Object with stops and the actual radius used
     */
    async getExpandedStops(
        location: Coordinates,
        excludeAtcoCodes: string[],
        currentRadius: number,
        maxResults = 6
    ): Promise<{ stops: NearbyBusStop[]; actualRadius: number }> {
        const config = getConfig();
        const excludeSet = new Set(excludeAtcoCodes);
        let searchRadius = currentRadius + config.busStops.radiusIncrement;

        // Try progressively larger radii until we find stops or hit max
        while (searchRadius <= config.busStops.maxExpandedRadius) {
            let nearbyStops: NearbyBusStop[] = [];
            try {
                nearbyStops = await this.findNearest(location, 100, searchRadius);
            } catch (error) {
                // A radius band with genuinely zero stops isn't a failure - just widen further
                if (!(error instanceof BusStopError) || !error.isNoStopsFound()) {
                    throw error;
                }
            }

            const newStops = nearbyStops
                // Only genuinely farther-out stops count as "new" - stops already within
                // currentRadius were simply excluded from the initial direction-limited list,
                // not undiscovered, so resurfacing them wouldn't be a useful expansion.
                .filter(
                    stop => stop.distanceMeters > currentRadius && !excludeSet.has(stop.atcoCode)
                )
                .slice(0, maxResults);

            if (newStops.length > 0) {
                return { stops: newStops, actualRadius: searchRadius };
            }

            // No new stops at this radius, try next level
            searchRadius += config.busStops.radiusIncrement;
        }

        // Reached max radius with no new stops
        return { stops: [], actualRadius: config.busStops.maxExpandedRadius };
    },

    /**
     * Refresh departures for both directions (4 stops total)
     */
    async refreshBothDirections(location: Coordinates): Promise<BothDirectionsResult> {
        try {
            const nearbyStops = await this.findNearest(location, 50);
            if (nearbyStops.length === 0) {
                throw new BusStopError(
                    'No bus stops found nearby',
                    BusStopErrorCode.NO_STOPS_FOUND
                );
            }

            const stopsToShow = selectBothDirectionStops(nearbyStops);

            // Fetch fresh departures for all stops in parallel with partial success handling
            const { boards, partialFailures } = await fetchBoardsWithPartialFailures(
                stopsToShow,
                async stop => {
                    const departures = await fetchDeparturesForStop(stop, 3);
                    await BusStopCache.setDepartures(stop.atcoCode, departures);
                    return {
                        stop,
                        departures,
                        lastUpdated: Date.now(),
                        isStale: false,
                    };
                }
            );

            const usefulBoards = selectUsefulBoards(boards);
            if (usefulBoards.length > 0) {
                return {
                    success: true,
                    boards: usefulBoards,
                    partialFailures: partialFailures.length > 0 ? partialFailures : undefined,
                };
            }

            throw new BusStopError(
                'Failed to refresh departures for all stops',
                BusStopErrorCode.DEPARTURES_UNAVAILABLE
            );
        } catch (error) {
            const busError =
                error instanceof BusStopError
                    ? error
                    : new BusStopError(String(error), BusStopErrorCode.DEPARTURES_UNAVAILABLE);

            return { success: false, error: busError };
        }
    },
};
